<script setup lang="ts">
import {
  getCategoryRoute,
  getTranslatedProperty,
} from "@shopware/helpers";
import { onKeyStroke, useActiveElement } from "@vueuse/core";
import type { Schemas } from "#shopware";
import { useMagicKeys } from "@vueuse/core";

type NavigationElement = Schemas["Category"] & {
  activeClass?: boolean;
};

const { navigationElements } = useNavigation();
const currentMenuPosition = ref<string | undefined>(undefined);
const resetActiveClass = ref<boolean>(true);

import { useMenuStore } from "@/stores/menu";

// Initialize the store
const menuStore = useMenuStore();

const route = useRoute();
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);
const { escape: escapeKey } = useMagicKeys();
const { isMobile } = useBreakpoint();

// Initialize review statistics early (shared state, non-blocking)
const { fetchReviewStatistics } = useReviewStatistics();

// Handler to prevent navigation on mobile for items with children
const handleNavigationClick = (event: MouseEvent, navigationElement: NavigationElement) => {
  // On mobile, prevent navigation if item has children (only toggle menu)
  if (isMobile.value && navigationElement.children && navigationElement.children.length > 0) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    updateActiveClass(navigationElement.id, navigationElement.parentId);
    handleTouchEvent(navigationElement.id, event);
    return false;
  } else {
    // On desktop or items without children, allow normal navigation
    updateActiveClass(navigationElement.id, navigationElement.parentId);
  }
};

onMounted(() => {
  let currentNavigationElement: NavigationElement | undefined;
  if (navigationElements.value) {
    currentNavigationElement = findNavigationElement(
      route.path.slice(1),
      navigationElements.value,
    );
  }
  if (currentNavigationElement) {
    updateActiveClass(
      currentNavigationElement.id,
      currentNavigationElement.parentId,
    );
  }

  // Fetch review statistics early in the background (non-blocking)
  // Using nextTick to ensure it doesn't block the initial render
  nextTick(() => {
    fetchReviewStatistics();
  });
});

const findNavigationElement = (
  routePath: string,
  navigation: NavigationElement[],
): NavigationElement | undefined => {
  for (let i = 0; i < navigation.length; i++) {
    const navigationElement = navigation[i];
    const seoUrls = navigationElement.seoUrls as
      | Schemas["SeoUrl"][]
      | undefined;
    if (seoUrls) {
      for (let j = 0; j < seoUrls.length; j++) {
        const currentSeoUrl = seoUrls[j];
        if (currentSeoUrl && currentSeoUrl.seoPathInfo === routePath) {
          return navigationElement;
        }
      }
    }
    const children = navigationElement.children;
    if (children) {
      const foundElement = findNavigationElement(routePath, children);
      if (foundElement) {
        return foundElement;
      }
    }
  }
  return undefined;
};

const onUpdateActiveClass = (
  navigationId: string,
  parentId: string | undefined,
) => {
  updateActiveClass(navigationId, parentId);
};

const resetNavigationActiveClass = (navigation: NavigationElement[]) => {
  for (let ni = 0; ni < navigation.length; ++ni) {
    navigation[ni].activeClass = false;
    const children = navigation[ni].children;
    if (children) {
      resetNavigationActiveClass(children);
    }
  }
};

const updateActiveClass = (
  navigationId: string,
  parentId: string | undefined,
) => {

  const setNavigationActiveClass = (
    navigation: NavigationElement[],
    navigationId: string,
    parentId: string | undefined,
  ) => {
    for (let ni = 0; ni < navigation.length; ++ni) {
      if (navigation[ni].id === navigationId) {
        navigation[ni].activeClass = true;
      }
      if (navigation[ni].id === parentId) {
        navigation[ni].activeClass = true;
        if (navigationElements.value) {
          setNavigationActiveClass(
            navigationElements.value,
            navigationId,
            navigation[ni].parentId,
          );
        }
      }
      const children = navigation[ni].children;
      if (children) {
        setNavigationActiveClass(children, navigationId, parentId);
      }
    }
  };

  if (navigationElements.value) {
    resetNavigationActiveClass(navigationElements.value);
    setNavigationActiveClass(navigationElements.value, navigationId, parentId);
    resetActiveClass.value = false;
  }
};
//#region - keyboard navigation (highliy connected to the HTML structure)
const activeElement = useActiveElement();

function isFocusInsideNav() {
  if (activeElement.value) {
    const focusInsideMenu = document
      .getElementsByTagName("nav")[0]
      .contains(activeElement.value);
    return focusInsideMenu;
  }
  return false;
}

onKeyStroke("Escape", () => {
  if (isFocusInsideNav()) {
    closeAllMenus();
    document
      .getElementsByTagName("header")[0]
      .getElementsByTagName("a")[0]
      .focus();
  }
});
//#endregion - keyboard navigation

const closeMobileMenu = () => {
  menuStore.isMobileMenuOpen = false;
  currentMenuPosition.value = undefined;
}

// Focus trap for mobile menu
const handleMobileMenuKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    event.preventDefault();
    closeMobileMenu();
    // Return focus to the menu trigger
    const menuTrigger = document.querySelector('[aria-controls="mobile-menu"]') as HTMLElement;
    if (menuTrigger) {
      menuTrigger.focus();
    }
  }
};
// reset when route.path changes
watch(
  () => route.path,
  () => {
    if (resetActiveClass.value && navigationElements.value) { 
      resetNavigationActiveClass(navigationElements.value); 
    }
    resetActiveClass.value = true; 
    closeAllMenus();

    // Ensure menu state is properly reset
    nextTick(() => {
      menuStore.closeMainMenu();
    });
  }
);

// Watch for changes in currentMenuPosition to update the body class
watch(
  () => currentMenuPosition.value,
  (newPosition) => {
    const isDesktop = !isMobile.value;
    if (isDesktop) {
      if (newPosition) {
        menuStore.openMainMenu();
      } else {
        menuStore.closeMainMenu();
      }
    }
  }
);

const isDesktop = computed(() => !isMobile.value);

const openMenu = (navigationId: string, hasChildren: boolean) => { 
  if (hasChildren) {
    menuStore.openMainMenu();
    currentMenuPosition.value = navigationId;
  }
};

const closeMenu = () => {
  menuStore.isMainMenuOpen = false;
  menuStore.isMobileMenuOpen = false;
  currentMenuPosition.value = undefined;
}

const closeDesktopMenu = () => {
  if (isDesktop.value) {
    menuStore.isMainMenuOpen = false;
    currentMenuPosition.value = undefined;
  }
}

const categoryNav = ref<HTMLElement | null>(null);
const handleTouchEvent = (navigationId: string, event?: Event) => {

  if (currentMenuPosition.value === navigationId) {
    currentMenuPosition.value = undefined;
    return;
  }

  currentMenuPosition.value = navigationId;

  if (categoryNav.value && !isDesktop.value) {
    nextTick(() => {
      const targetElement = event?.currentTarget as HTMLElement;
      if (targetElement) {
        const elementTop = targetElement.offsetTop;
        categoryNav.value?.scrollTo({
          top: elementTop - 60,
          behavior: "smooth",
        });
      }
    });
  }
}

onClickOutside(categoryNav, (event) => {
  if (menuStore.isMobileMenuOpen) {
    closeMobileMenu();
  }
});

const openDesktopMenu = (navigationElement: any) => {
  if (isDesktop.value) {
    openMenu(navigationElement.id, navigationElement.children.length > 0)
  }
}

const handleClickEvent = (navigationElement: any , event?: Event) => {
  // On mobile, if item has children, prevent navigation (handled by NuxtLink click)
  if (!isDesktop.value && navigationElement.children && navigationElement.children.length > 0) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    return;
  }
  
  if (!isDesktop.value) {
    if (!navigationElement.children || navigationElement.children.length === 0) {
      // Close the menu and redirect to the category page
      closeMobileMenu();
      navigateTo(formatLink(getCategoryRoute(navigationElement)));
    } else {
      handleTouchEvent(navigationElement.id, event);
    }
  } else {
    // For desktop, close the menu when clicking a menu item
    closeDesktopMenu();
    navigateTo(formatLink(getCategoryRoute(navigationElement)));
  }
}

const closeAllMenus = () => {
  if (navigationElements.value) {
    resetNavigationActiveClass(navigationElements.value);
  }
  currentMenuPosition.value = undefined;
  menuStore.closeMobileMenu();
  menuStore.closeMainMenu();
};

// Focus management for accessibility
const focusFirstMenuItem = () => {
  const firstMenuItem = document.querySelector('[role="menuitem"]') as HTMLElement;
  if (firstMenuItem) {
    firstMenuItem.focus();
  }
};

const focusLastMenuItem = () => {
  const menuItems = document.querySelectorAll('[role="menuitem"]');
  const lastMenuItem = menuItems[menuItems.length - 1] as HTMLElement;
  if (lastMenuItem) {
    lastMenuItem.focus();
  }
};

// Add keyboard navigation for menu items
const handleKeyDown = (event: KeyboardEvent, navigationElement: any) => {
  const target = event.target as HTMLElement;
  const menuItem = target.closest('li');
  const menuItems = menuItem?.parentElement?.querySelectorAll('li');
  
  if (!menuItems || !menuItem) return;
  
  const currentIndex = Array.from(menuItems).indexOf(menuItem);
  let nextIndex = currentIndex;
  
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      // Open dropdown if navigation element has children
      if (navigationElement.children && navigationElement.children.length > 0) {
        currentMenuPosition.value = navigationElement.id;
        // Focus on the first submenu item if available
        const firstSubmenuItem = menuItem?.querySelector('[role="menu"] [role="menuitem"]') as HTMLElement;
        if (firstSubmenuItem) {
          firstSubmenuItem.focus();
        }
        return;
      }
      // If no dropdown, navigate to next menu item
      nextIndex = (currentIndex + 1) % menuItems.length;
      break;
    case 'ArrowUp':
      event.preventDefault();
      // Close dropdown if currently open
      if (currentMenuPosition.value === navigationElement.id) {
        currentMenuPosition.value = undefined;
        return;
      }
      // Navigate to previous menu item
      nextIndex = currentIndex === 0 ? menuItems.length - 1 : currentIndex - 1;
      break;
    case 'Enter':
    case ' ':
      event.preventDefault();
      handleClickEvent(navigationElement, event);
      return;
    case 'Escape':
      event.preventDefault(); 
      closeAllMenus();
      return;
    case 'Home':
      event.preventDefault();
      focusFirstMenuItem();
      return;
    case 'End':
      event.preventDefault();
      focusLastMenuItem();
      return;
  }
  
  const nextMenuItem = menuItems[nextIndex];
  const nextLink = nextMenuItem?.querySelector('[role="menuitem"]') as HTMLElement;
  if (nextLink) {
    nextLink.focus();
  }
};

// NOTE: Do it with out  watch - refer lagerwerk
watch(escapeKey, (newVal) => {
  if (newVal && menuStore.isMobileMenuOpen) {
    closeMobileMenu();
  }
});

const { logoData } = useLogo();
</script>

<template>
  <!-- eslint-disable vue/no-v-html -->
  <nav
    id="mobile-menu"
    class="category-nav opacity-0 invisible lg:opacity-100 lg:visible overflow-y-scroll lg:overflow-y-visible bg-white fixed inset-0 lg:sticky lg:top-[118px] z-[45] lg:z-[45] block lg:flex lg:flex-col text-base 3xl:text-md text-center border-b border-grey-100 w-[85%] md:w-3/6 lg:w-full -translate-x-full md:translate-x-0"
    :aria-label="$t('header.ariaLabelMainNavigation')"
    ref="categoryNav"
    role="navigation"
    @keydown="handleMobileMenuKeydown"
  >

    <div class="nav-header py-4 block lg:hidden sticky top-0 bg-white z-10 border-b border-grey-100">
      <div class="container">
        <div class="flex justify-between items-center">
          <NuxtLink to="/" :aria-label="$t('header.ariaLabelHome')">
            <img
              :src="logoData?.url"
              :alt="logoData?.alt ?? $t('header.ariaLabelLogo')"
              :height="logoData?.height ?? 56"
              :width="logoData?.width ?? 322"
              class="w-[160px] md:w-[200px] lg:w-[260px] xl:w-[322px]" />
          </NuxtLink>
          <button 
            class="text-xl" 
            @click="closeMobileMenu()" 
            :aria-label="$t('header.ariaLabelClose')" 
            id="menuClose"
            aria-controls="mobile-menu"
          >
            <i class="icon-close-outline" aria-hidden="true"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- TODO: bredcrumb overflow issue needs to properly fixed -->
    <!-- Current fix: truncate -->
    <div class="breadcrumb block lg:hidden bg-primary/5 truncate">
      <LayoutBreadcrumbs />
    </div>  
    <div class="categories py-4 lg:py-0 h-fit overflow-hidden lg:overflow-visible">
      <div class="container z-0">
        <!-- Do not use overflow-x-auto on this wrapper: it clips overflow-y and hides lg:absolute megamenus in WebKit. -->
        <div class="overflow-hidden hide-scrollbar scroll-smooth" data-menu-scroller>
          <span class="text-md font-bold text-black text-left block lg:hidden mt-4">{{ $t('header.categoryText') }}</span>
          <ul role="menubar" class="flex flex-col lg:flex-row justify-between gap-0 mt-2 lg:mt-0 lg:gap-6 items-start whitespace-nowrap list-none">

            <li
              v-if="navigationElements"
              v-for="(navigationElement, index) in navigationElements"
              :key="navigationElement.id"
              role="none"
              class="menu-item lg:flex items-center w-full lg:w-auto"
              @mouseenter="openDesktopMenu(navigationElement)"
              @mouseleave="closeDesktopMenu()"
              :class="{
                'menu-item-open': currentMenuPosition === navigationElement.id,
                'group gap-0 has-child md:gap-2.5': navigationElement.children.length > 0,
                'gap-2.5 lg:block': navigationElement.children.length === 0,
                'border-b border-grey-100 md:border-b-0': index !== navigationElements.length - 1
              }"
            >
              <NuxtLink
                role="menuitem"
                :target="navigationElement.externalLink || navigationElement.linkNewTab ? '_blank' : ''"
                :to="isMobile && navigationElement.children.length > 0 ? '#' : formatLink(getCategoryRoute(navigationElement))"
                :class="{ 'link-active': (navigationElement as NavigationElement).activeClass }"
                class="py-4 md:py-3 hover:text-primary focus:text-primary transition-colors duration-300 ease-in-out flex lg:inline-flex items-center justify-between lg:justify-center gap-[6px] w-full lg:w-auto"
                @click="handleNavigationClick($event, navigationElement)"
                @keydown="handleKeyDown($event, navigationElement)"
                :aria-haspopup="navigationElement.children.length > 0 ? 'true' : undefined"
                :aria-expanded="currentMenuPosition === navigationElement.id"
                :tabindex="0"
              >
                <span class="ml-0 mr-auto lg:my-auto font-medium text-base 3xl:text-md lg:font-medium text-grey-700">{{ getTranslatedProperty(navigationElement, "name") }}</span>
                
                <i
                  v-if="navigationElement.children.length > 0"
                  class="icon-chevron-right block  md:hidden text-md lg:text-xs transition-transform duration-300"
                  :aria-hidden="true"></i>
                <i 
                  v-if="navigationElement.children.length > 0"
                  class="icon-chevron-down hidden font-bold md:block text-md lg:text-xs transition-transform duration-300 relative top-[2px]"
                  :aria-hidden="true"></i>
              </NuxtLink>
              
              <div
                v-if="navigationElement.children.length > 0"
                role="menu"
                :aria-label="getTranslatedProperty(navigationElement, 'name') + ' submenu'"
                :aria-hidden="currentMenuPosition !== navigationElement.id"
                class="dropdown-menu-item transition-all duration-300 ease-in-out pointer-events-none group-hover:pointer-events-auto relative lg:absolute left-0 top-full w-full bg-white md:border-t md:border-grey-100 h-0 lg:h-auto"
                :class="{
                  'group-hover/link:opacity-100 group-hover/link:visible h-auto': currentMenuPosition === navigationElement.id,
                  'opacity-0 invisible h-0': currentMenuPosition !== navigationElement.id,
                }"
                @focusin="currentMenuPosition = navigationElement.id"
                @mouseleave="currentMenuPosition = undefined"
                @focusout="currentMenuPosition = undefined"
                @click.capture="closeMenu()"
              >
                <div
                  class="container pt-4 pb-6 md:py-12 px-0 md:px-5"
                >
                  <div
                      class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 3xl:grid-cols-7 gap-4 md:gap-6">
                    <template v-if="navigationElement.children.length > 0">
                      <LayoutTopNavigationRecursive
                        :navigation-element-children="navigationElement.children"
                        :parent-category="navigationElement"
                        :last-element="
                          navigationElements
                            ? navigationElements.length - 1 === index
                            : false
                        "
                        @update-active-class="onUpdateActiveClass"
                        @focusout-last-element="currentMenuPosition = undefined"
                        :is-menu-open="currentMenuPosition === navigationElement.id"
                        @close-menu="closeMenu"
                      />
                    </template>
                  </div>
                </div>
              </div>
            </li>

          </ul>
        </div>
      </div>  
    </div>

    <div class="information block lg:hidden bg-primary/5 text-left py-8">
      <LayoutHeaderSidebarInformation />
    </div>

    <LayoutHeaderMenuScroll />

  </nav>

</template>